import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  Inbox,
  Loader2,
  RefreshCw,
  Search,
} from "lucide-react";

import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ApiError,
  getAccountTransactions,
  syncPlaidTransactions,
} from "@/lib/api";
import { formatAbsoluteDateTime, formatCurrency } from "@/lib/format";
import { cn } from "@/lib/utils";
import type {
  AccountTransactionsResponse,
  AccountTransactionsSummary,
  Transaction,
  TransactionDirection,
} from "@finance/shared";

type LoadState =
  | { status: "loading" }
  | { status: "ready"; data: AccountTransactionsResponse }
  | { status: "error"; message: string };

const POLL_INTERVAL_MS = 5_000;
const MAX_POLL_ATTEMPTS = 6;
const SEARCH_DEBOUNCE_MS = 300;

export function AccountTransactionsPage() {
  const params = useParams<{ accountId: string }>();
  const accountId = params.accountId ?? "";

  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  // Active direction filter (FIN-43). `null` = no filter / show all.
  // Income and Spending are mutually exclusive; clicking the active one
  // toggles back to `null`.
  const [direction, setDirection] = useState<TransactionDirection | null>(null);
  // What the user has typed into the toolbar search input (live, every
  // keystroke). The debounced version below is what actually triggers a
  // refetch.
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // Number of times we've polled while waiting for the initial post-Link
  // sync to land. Capped at MAX_POLL_ATTEMPTS per the issue so a stuck
  // sync doesn't hammer the API indefinitely.
  const [pollAttempts, setPollAttempts] = useState(0);
  // Reset the poll counter when the user navigates to a different account
  // so the new mount doesn't inherit the previous account's exhausted
  // budget.
  const lastAccountIdRef = useRef<string | null>(null);

  // Debounce the search input so we don't fire a request on every
  // keystroke. 300ms is the same value the topbar uses elsewhere and
  // matches typical UI conventions.
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const load = useCallback(
    async (
      dir: TransactionDirection | null,
      search: string,
      signal?: AbortSignal,
    ): Promise<void> => {
      try {
        const data = await getAccountTransactions(accountId, {
          direction: dir,
          search: search || null,
          signal,
        });
        if (signal?.aborted) return;
        setState({ status: "ready", data });
      } catch (err: unknown) {
        if (signal?.aborted) return;
        setState({
          status: "error",
          message: errorMessage(err),
        });
      }
    },
    [accountId],
  );

  useEffect(() => {
    if (lastAccountIdRef.current !== accountId) {
      lastAccountIdRef.current = accountId;
      setPollAttempts(0);
      setSyncError(null);
      setDirection(null);
      setSearchInput("");
      setDebouncedSearch("");
      setState({ status: "loading" });
    }
    const ctrl = new AbortController();
    void load(direction, debouncedSearch, ctrl.signal);
    return () => ctrl.abort();
  }, [accountId, direction, debouncedSearch, load]);

  // Polling: while the post-Link initial sync hasn't finished yet, refetch
  // every 5s up to MAX_POLL_ATTEMPTS so the page transitions from "still
  // syncing" → "list" on its own. Stops the moment the sync stamps
  // `initial_transactions_synced_at`, returns transactions, or the user
  // navigates away.
  useEffect(() => {
    if (state.status !== "ready") return;
    if (!isWaitingForInitialSync(state.data)) return;
    if (pollAttempts >= MAX_POLL_ATTEMPTS) return;
    const handle = setTimeout(() => {
      setPollAttempts((n) => n + 1);
      void load(direction, debouncedSearch);
    }, POLL_INTERVAL_MS);
    return () => clearTimeout(handle);
  }, [state, pollAttempts, load, direction, debouncedSearch]);

  const handleSync = useCallback(async () => {
    if (state.status !== "ready") return;
    const itemId = state.data.account.plaid_item?.item_id;
    if (!itemId) return;
    setSyncing(true);
    setSyncError(null);
    try {
      await syncPlaidTransactions(itemId);
      await load(direction, debouncedSearch);
    } catch (err: unknown) {
      setSyncError(errorMessage(err));
    } finally {
      setSyncing(false);
    }
  }, [load, state, direction, debouncedSearch]);

  // FIN-43: Income / Spending are mutually exclusive — clicking the active
  // pill clears the filter, clicking the other one swaps. The effect above
  // reacts to the new `direction` and refetches.
  const handleToggleDirection = useCallback(
    (next: TransactionDirection) => {
      setDirection((current) => (current === next ? null : next));
    },
    [],
  );

  const handleClearFilters = useCallback(() => {
    setDirection(null);
    setSearchInput("");
    setDebouncedSearch("");
  }, []);

  return (
    <div className="space-y-6">
      <BackLink />
      {state.status === "loading" ? (
        <HeaderSkeleton />
      ) : state.status === "error" ? (
        <ErrorState
          message={state.message}
          onRetry={() => void load(direction, debouncedSearch)}
        />
      ) : (
        <ReadyView
          data={state.data}
          syncing={syncing}
          syncError={syncError}
          onSync={() => void handleSync()}
          pollAttempts={pollAttempts}
          direction={direction}
          onToggleDirection={handleToggleDirection}
          searchInput={searchInput}
          onSearchChange={setSearchInput}
          activeSearch={debouncedSearch}
          onClearFilters={handleClearFilters}
        />
      )}
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/accounts"
      className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
    >
      <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
      Accounts
    </Link>
  );
}

interface ReadyViewProps {
  data: AccountTransactionsResponse;
  syncing: boolean;
  syncError: string | null;
  onSync: () => void;
  pollAttempts: number;
  direction: TransactionDirection | null;
  onToggleDirection: (next: TransactionDirection) => void;
  searchInput: string;
  onSearchChange: (value: string) => void;
  activeSearch: string;
  onClearFilters: () => void;
}

function ReadyView({
  data,
  syncing,
  syncError,
  onSync,
  pollAttempts,
  direction,
  onToggleDirection,
  searchInput,
  onSearchChange,
  activeSearch,
  onClearFilters,
}: ReadyViewProps) {
  const { account, transactions, summary } = data;
  const canSync = account.source === "api" && !!account.plaid_item?.item_id;
  const lastSyncedAt =
    account.plaid_item?.last_synced_at ?? account.last_synced_at;
  const waitingForInitialSync = isWaitingForInitialSync(data);
  const pollExhausted = waitingForInitialSync && pollAttempts >= MAX_POLL_ATTEMPTS;
  // Any active filter, used to decide between "no matching" and "synced
  // but empty" empty states (FIN-43).
  const hasActiveFilter = direction != null || activeSearch !== "";
  const filteredEmpty =
    account.source === "api" &&
    !waitingForInitialSync &&
    hasActiveFilter &&
    transactions.length === 0;
  // The toolbar lives on the transactions card and only makes sense for
  // api accounts that have moved past initial sync. Manual accounts and
  // accounts still doing the post-Link bulk pull get the standalone
  // empty card / sync card instead.
  const showToolbarCard =
    account.source === "api" && !waitingForInitialSync;

  return (
    <>
      <PageHeader
        eyebrow={account.institution}
        title={account.name}
        description={buildMetadataLine(account, lastSyncedAt)}
      />

      {syncError ? (
        <p
          role="status"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
        >
          {syncError}
        </p>
      ) : null}

      {account.plaid_item?.last_sync_error && !syncError ? (
        <p
          role="status"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
        >
          Last sync failed: {account.plaid_item.last_sync_error}
        </p>
      ) : null}

      {account.source !== "api" ? (
        <EmptyCard
          icon={<Inbox className="h-5 w-5" aria-hidden />}
          title="No auto-synced transactions"
          message="This account is manual, so we don't pull transactions for it. Connect the account via Plaid to see synced activity here."
        />
      ) : waitingForInitialSync ? (
        <SyncingCard exhausted={pollExhausted} />
      ) : showToolbarCard ? (
        <Card className="overflow-hidden">
          <TransactionsToolbar
            summary={summary}
            direction={direction}
            onToggleDirection={onToggleDirection}
            searchValue={searchInput}
            onSearchChange={onSearchChange}
            canSync={canSync}
            syncing={syncing}
            onSync={onSync}
          />
          {filteredEmpty ? (
            <FilteredEmptyState onClear={onClearFilters} />
          ) : transactions.length === 0 ? (
            <SyncedEmptyState />
          ) : (
            <TransactionsTable rows={transactions} />
          )}
        </Card>
      ) : null}
    </>
  );
}

interface TransactionsToolbarProps {
  summary: AccountTransactionsSummary;
  direction: TransactionDirection | null;
  onToggleDirection: (next: TransactionDirection) => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  canSync: boolean;
  syncing: boolean;
  onSync: () => void;
}

// Toolbar lives at the top of the transactions card. Left side: free-text
// search input. Right side: Income / Spending direction badges + the sync
// icon button. Wraps onto a second row on narrow viewports.
function TransactionsToolbar({
  summary,
  direction,
  onToggleDirection,
  searchValue,
  onSearchChange,
  canSync,
  syncing,
  onSync,
}: TransactionsToolbarProps) {
  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-border px-3 py-2.5 sm:px-4">
      <div className="relative w-full min-w-[12rem] sm:w-64 sm:flex-1 sm:max-w-sm">
        <Search
          aria-hidden
          className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          type="search"
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search transactions…"
          aria-label="Search transactions"
          className="h-9 pl-8"
        />
      </div>
      <div className="ml-auto flex flex-wrap items-center gap-2">
        <DirectionBadge
          kind="income"
          label="Income"
          amount={summary.income_total}
          currency={summary.currency}
          active={direction === "income"}
          onClick={() => onToggleDirection("income")}
        />
        <DirectionBadge
          kind="spending"
          label="Spending"
          amount={summary.spending_total}
          currency={summary.currency}
          active={direction === "spending"}
          onClick={() => onToggleDirection("spending")}
        />
        {canSync ? (
          <Button
            variant="ghost"
            size="icon"
            onClick={onSync}
            disabled={syncing}
            aria-label={syncing ? "Syncing transactions" : "Sync transactions"}
            title={syncing ? "Syncing…" : "Sync now"}
            className="h-9 w-9 text-muted-foreground hover:text-foreground"
          >
            <RefreshCw
              className={cn("h-4 w-4", syncing && "animate-spin")}
              aria-hidden
            />
          </Button>
        ) : null}
      </div>
    </div>
  );
}

interface DirectionBadgeProps {
  kind: TransactionDirection;
  label: string;
  amount: number;
  currency: string;
  active: boolean;
  onClick: () => void;
}

// Rectangular, outlined themed badge (emerald for income, rose for
// spending) with a nested mini-badge that always shows the user-facing
// positive total (db-wide, not affected by the active filter, the search
// box, or pagination). Active state swaps to filled.
function DirectionBadge({
  kind,
  label,
  amount,
  currency,
  active,
  onClick,
}: DirectionBadgeProps) {
  const palette =
    kind === "income"
      ? {
          outlined:
            "border-emerald-500/70 text-emerald-700 hover:bg-emerald-500/10 dark:text-emerald-400 dark:hover:bg-emerald-500/15",
          filled:
            "border-emerald-500 bg-emerald-500 text-white dark:border-emerald-400 dark:bg-emerald-400 dark:text-emerald-950",
          miniOutlined:
            "bg-emerald-500/10 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
          miniFilled:
            "bg-white/20 text-white dark:bg-emerald-950/30 dark:text-emerald-950",
        }
      : {
          outlined:
            "border-rose-500/70 text-rose-700 hover:bg-rose-500/10 dark:text-rose-400 dark:hover:bg-rose-500/15",
          filled:
            "border-rose-500 bg-rose-500 text-white dark:border-rose-400 dark:bg-rose-400 dark:text-rose-950",
          miniOutlined:
            "bg-rose-500/10 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300",
          miniFilled:
            "bg-white/20 text-white dark:bg-rose-950/30 dark:text-rose-950",
        };
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "inline-flex items-center gap-2 rounded-md border bg-transparent px-2.5 py-1 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        active ? palette.filled : palette.outlined,
      )}
    >
      <span>{label}</span>
      <span
        className={cn(
          "inline-flex items-center rounded-sm px-1.5 py-0.5 font-mono text-[11px] tabular-nums",
          active ? palette.miniFilled : palette.miniOutlined,
        )}
      >
        {formatAmount(amount, currency)}
      </span>
    </button>
  );
}

function FilteredEmptyState({ onClear }: { onClear: () => void }) {
  return (
    <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
      <div className="grid h-10 w-10 place-items-center rounded-md border border-border bg-muted text-muted-foreground">
        <Inbox className="h-5 w-5" aria-hidden />
      </div>
      <p className="text-sm font-medium text-foreground">
        No matching transactions
      </p>
      <p className="max-w-md text-sm text-muted-foreground">
        No transactions match the current filter for this account.
      </p>
      <Button variant="outline" size="sm" onClick={onClear}>
        Clear filter
      </Button>
    </CardContent>
  );
}

function SyncedEmptyState() {
  return (
    <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
      <div className="grid h-10 w-10 place-items-center rounded-md border border-border bg-muted text-muted-foreground">
        <Inbox className="h-5 w-5" aria-hidden />
      </div>
      <p className="text-sm font-medium text-foreground">
        No transactions in the last 30 days
      </p>
      <p className="max-w-md text-sm text-muted-foreground">
        Plaid hasn't returned any activity for this account in the last 30 days.
      </p>
    </CardContent>
  );
}

function buildMetadataLine(
  account: AccountTransactionsResponse["account"],
  lastSyncedAt: string | null,
): string {
  const parts: string[] = [];
  if (account.mask) parts.push(`••${account.mask}`);
  parts.push(formatCurrency(account.current_balance));
  if (account.source === "api") {
    parts.push(
      lastSyncedAt
        ? `Last synced ${formatAbsoluteDateTime(lastSyncedAt)}`
        : "Never synced",
    );
  }
  return parts.join(" · ");
}

interface TransactionsTableProps {
  rows: Transaction[];
}

function TransactionsTable({ rows }: TransactionsTableProps) {
  // table-fixed + explicit widths on Date / Category / Amount let the Name
  // column absorb the remaining width and truncate long values with an
  // ellipsis (FIN-49). Without table-fixed, a 150-char merchant name
  // would push Category and Amount past the card's right edge.
  return (
    <Table className="table-fixed">
      <TableHeader>
        <TableRow>
          <TableHead className="w-[110px] whitespace-nowrap">Date</TableHead>
          <TableHead className="whitespace-nowrap">Name</TableHead>
          <TableHead className="w-[180px] whitespace-nowrap">Category</TableHead>
          <TableHead className="w-[130px] whitespace-nowrap text-right">
            Amount
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((tx) => (
          <TransactionRow key={tx.id} tx={tx} />
        ))}
      </TableBody>
    </Table>
  );
}

function TransactionRow({ tx }: { tx: Transaction }) {
  const displayName = tx.merchant_name?.trim() || tx.name;
  // Plaid amount sign convention: positive = money leaves the visible
  // balance (purchase / withdrawal), negative = money comes in (refund /
  // deposit / credit card payment). Invert for the user-facing view so a
  // purchase shows as a red negative and an inflow shows as a green
  // positive — applies uniformly to bank accounts and credit cards.
  const displayed = -tx.amount;
  const formattedAmount = formatAmount(displayed, tx.currency ?? null);
  const category = formatCategory(tx);
  return (
    <TableRow className={cn(tx.pending && "text-muted-foreground")}>
      <TableCell className="whitespace-nowrap font-mono tabular-nums text-xs">
        {formatTxDate(tx.date)}
      </TableCell>
      <TableCell className="overflow-hidden">
        {/* min-w-0 lets the truncate span shrink below its content width
            inside the flex row so the ellipsis kicks in; the Pending badge
            stays at intrinsic size via shrink-0. */}
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="truncate font-medium text-foreground"
            title={displayName}
          >
            {displayName}
          </span>
          {tx.pending ? (
            <Badge
              variant="outline"
              className="shrink-0 border-muted-foreground/30 bg-muted/40 text-[10px] uppercase tracking-wide text-muted-foreground"
            >
              Pending
            </Badge>
          ) : null}
        </div>
      </TableCell>
      <TableCell className="overflow-hidden text-muted-foreground text-xs">
        <span className="block truncate" title={category}>
          {category}
        </span>
      </TableCell>
      <TableCell
        className={cn(
          "whitespace-nowrap text-right font-mono tabular-nums",
          displayed < 0 ? "text-destructive" : "text-success",
        )}
      >
        {formattedAmount}
      </TableCell>
    </TableRow>
  );
}

function SyncingCard({ exhausted }: { exhausted: boolean }) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
        <Loader2
          className={cn(
            "h-5 w-5 text-muted-foreground",
            !exhausted && "animate-spin",
          )}
          aria-hidden
        />
        <p className="text-sm font-medium text-foreground">
          {exhausted
            ? "Still syncing in the background"
            : "Transactions are still syncing"}
        </p>
        <p className="max-w-md text-sm text-muted-foreground">
          {exhausted
            ? "This is taking longer than expected. Reload the page in a minute, or use Sync now to retry."
            : "Hang tight — Plaid is pulling the last 30 days of activity. This usually takes less than a minute."}
        </p>
      </CardContent>
    </Card>
  );
}

function EmptyCard({
  icon,
  title,
  message,
}: {
  icon: React.ReactNode;
  title: string;
  message: string;
}) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
        <div className="grid h-10 w-10 place-items-center rounded-md border border-border bg-muted text-muted-foreground">
          {icon}
        </div>
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="max-w-md text-sm text-muted-foreground">{message}</p>
      </CardContent>
    </Card>
  );
}

function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <Card className="border-destructive/40">
      <CardContent className="flex items-start gap-3 py-6">
        <AlertTriangle
          className="mt-0.5 h-5 w-5 text-destructive"
          aria-hidden
        />
        <div className="flex-1 space-y-2">
          <p className="text-sm font-medium text-foreground">
            Couldn't load transactions
          </p>
          <p className="font-mono text-xs text-muted-foreground">{message}</p>
          <Button variant="outline" size="sm" onClick={onRetry}>
            Try again
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function HeaderSkeleton() {
  return (
    <div className="space-y-6">
      <div className="space-y-2 border-b border-border pb-6">
        <div className="h-3 w-32 animate-pulse rounded bg-muted/60" />
        <div className="h-6 w-64 animate-pulse rounded bg-muted/60" />
        <div className="h-3 w-80 animate-pulse rounded bg-muted/60" />
      </div>
      <Card>
        <CardContent className="space-y-3 p-6">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className={cn(
                "h-9 rounded-md bg-muted/50 animate-pulse",
                i === 4 && "w-2/3",
              )}
            />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function isWaitingForInitialSync(data: AccountTransactionsResponse): boolean {
  const { account, summary } = data;
  if (account.source !== "api") return false;
  const item = account.plaid_item;
  // No item row at all → there's no background sync to wait for. The
  // backend already returned what it has; just render empty.
  if (!item) return false;
  // Sync still in flight: the post-exchange task hasn't stamped
  // `initial_transactions_synced_at` yet. We check `summary` totals rather
  // than the (possibly direction-filtered) `transactions` array so an
  // active Income/Spending filter doesn't make a synced account look like
  // it's still syncing (FIN-43).
  return (
    item.initial_transactions_synced_at == null &&
    summary.income_total === 0 &&
    summary.spending_total === 0
  );
}

const txDateFormatter = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
});

function formatTxDate(iso: string): string {
  // Plaid sends YYYY-MM-DD. Append a UTC time so Date.parse doesn't drift
  // by the viewer's timezone offset (which would otherwise render
  // 2026-05-15 as "May 14" west of UTC).
  const ts = Date.parse(`${iso}T00:00:00Z`);
  if (Number.isNaN(ts)) return iso;
  return txDateFormatter.format(new Date(ts));
}

// FIN-48: prefer Plaid's newer `personal_finance_category` taxonomy when
// available, falling back to the legacy `category` path on rows that
// pre-date the migration. The detailed enum from Plaid looks like
// `FOOD_AND_DRINK_RESTAURANTS`; we strip the primary prefix when it
// duplicates the start of the detailed value, then humanize what's left.
function formatCategory(tx: Transaction): string {
  const detailed = tx.personal_finance_category_detailed;
  if (detailed) {
    const primary = tx.personal_finance_category_primary;
    const trimmed =
      primary && detailed.startsWith(`${primary}_`)
        ? detailed.slice(primary.length + 1)
        : detailed;
    return humanizeEnumToken(trimmed);
  }
  return tx.category ?? "—";
}

function humanizeEnumToken(token: string): string {
  if (!token) return "—";
  return token
    .toLowerCase()
    .split("_")
    .filter((part) => part.length > 0)
    .map((part) => part[0]!.toUpperCase() + part.slice(1))
    .join(" ");
}

const usdFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 2,
});

function formatAmount(value: number, currency: string | null): string {
  if (!currency || currency === "USD") {
    return usdFormatter.format(value);
  }
  // Build a formatter on-demand for non-USD currencies. Intl will throw on
  // unrecognized codes — fall back to USD-style formatting in that case so
  // the row still renders.
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return usdFormatter.format(value);
  }
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    return err.serverMessage ?? err.message;
  }
  if (err instanceof Error) return err.message;
  return "Unknown error";
}
